import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/layout/AdminLayout";
import { useToast } from "@/hooks/use-toast";
import { Tables } from "@/integrations/supabase/types";
import { HologramGeneratorForm, type GeneratorForm } from "@/components/hologram/HologramGeneratorForm";
import { HologramPreview } from "@/components/hologram/HologramPreview";

type PrintServiceProvider = Tables<"print_service_providers">;

export default function HologramGenerator() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [generatedCodes, setGeneratedCodes] = useState<string[]>([]);

  const { data: providers, isLoading: isLoadingProviders } = useQuery({
    queryKey: ["print-providers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("print_service_providers")
        .select("*")
        .eq("is_active", true)
        .order("name");

      if (error) throw error;
      return data as PrintServiceProvider[];
    },
  });

  const generateHologramsMutation = useMutation({
    mutationFn: async (params: { codes: string[], providerId: string }) => {
      const providerId = parseInt(params.providerId);
      
      if (isNaN(providerId)) {
        throw new Error("Invalid provider ID");
      }

      // First check if any of the codes already exist for this provider
      const { data: existingCodes, error: checkError } = await supabase
        .from("holograms")
        .select("hologram_code")
        .eq("provider_id", providerId)
        .in("hologram_code", params.codes);

      if (checkError) throw checkError;

      if (existingCodes && existingCodes.length > 0) {
        const duplicateCodes = existingCodes.map(code => code.hologram_code).join(", ");
        throw new Error(`The following hologram codes already exist for this provider: ${duplicateCodes}`);
      }

      const { error } = await supabase.from("holograms").insert(
        params.codes.map((code) => ({
          hologram_code: code,
          provider_id: providerId,
          status: "available" as const,
        }))
      );
      
      if (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["holograms"] });
      toast({
        title: "Success",
        description: `Generated ${generatedCodes.length} hologram codes`,
      });
      setGeneratedCodes([]);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const generateSequence = (values: GeneratorForm) => {
    const start = parseInt(values.startNumber);
    const end = parseInt(values.endNumber);
    const digits = parseInt(values.digitCount);
    const codes: string[] = [];

    for (let i = start; i <= end; i++) {
      const number = i.toString().padStart(digits, "0");
      const code = `${values.prefix}${number}${values.suffix}`;
      codes.push(code);
    }

    return codes;
  };

  const onSubmit = (values: GeneratorForm) => {
    if (!values.providerId) {
      toast({
        title: "Error",
        description: "Please select a print provider",
        variant: "destructive",
      });
      return;
    }
    const codes = generateSequence(values);
    setGeneratedCodes(codes);
  };

  const handleSave = () => {
    if (generatedCodes.length > 0) {
      generateHologramsMutation.mutate({ 
        codes: generatedCodes,
        providerId: providers?.[0]?.provider_id.toString() ?? ""
      });
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Hologram Generator</h1>
        </div>

        <div className="grid grid-cols-1 gap-6 max-w-xl">
          <HologramGeneratorForm 
            providers={providers}
            onSubmit={onSubmit}
            isLoading={isLoadingProviders}
          />
          
          <HologramPreview 
            codes={generatedCodes}
            onSave={handleSave}
            isLoading={generateHologramsMutation.isPending}
          />
        </div>
      </div>
    </AdminLayout>
  );
}